import * as React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import {
  InputBaseContainer,
  InputTextWithLabel,
  TextAreaWithLabel,
  CheckboxWithLabel,
  ButtonWithLoader,
} from '../molecules';
import {
  getAssignableUsers,
  AssignableUser,
  SpecialPermissionType,
} from '../api/specialPermission';
import { FormServerError } from '../types';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';

const labels = {
  name: 'Permission Name',
  permissionType: 'Type',
  description: 'Description',
  assignedUser: 'Assigned User',
  isActive: 'Active',
};

// Plain `.required()` on purpose: @hookform/resolvers is v0.1.1, where yup
// transforms like `.trim()` interact badly with the resolver. The server
// trims and re-validates both fields anyway.
const schema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  permissionType: yup.string().required().label(labels.permissionType),
  description: yup.string().label(labels.description),
});

/**
 * Settings > Special Permission, edit modal (EA-1343).
 *
 * Name, type and description are editable display copy. The row's `key` is
 * not shown or editable: it is the handle the automations look up, so
 * renaming it would silently detach a permission from its setting.
 */
const SpecialPermissionForm = ({
  onSubmit,
  modalError,
  initialValues,
  addButtonText = 'Add',
  updateButtonText = 'Save',
  initialErrors,
}: Props) => {
  const isNew = !initialValues?.id;
  const {
    register,
    handleSubmit,
    errors,
    setError,
    control,
    formState,
  } = useForm<SpecialPermissionType>({
    resolver: yupResolver(schema),
    // 'User' is the only type the seeded rows use, so it is the sensible
    // starting point for a new one rather than an empty required field.
    defaultValues:
      initialValues ||
      ({ permissionType: 'User' } as SpecialPermissionType),
  });

  const [users, setUsers] = React.useState<AssignableUser[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    let active = true;
    getAssignableUsers()
      .then(response => {
        if (active) {
          setUsers(response.data || []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (formState.isSubmitting && !Object.keys(errors).length) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<SpecialPermissionType>(
        initialErrors,
      ).forEach(({ name, types }) => setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (
    submitFn: (data: SpecialPermissionType) => void,
  ) => (data: SpecialPermissionType) =>
    submitFn({
      ...data,
      id: initialValues?.id,
      // '' is the "Nobody" option -- send null so the server clears the FK
      // rather than failing to parse an empty string as a pk.
      assignedUser: data.assignedUser ? Number(data.assignedUser) : null,
    });

  const displayName = (user: AssignableUser) =>
    [user?.name, user?.email].filter(Boolean).join(' - ');

  return (
    <Container disabled={isLoading || (isSubmitting && !modalError)}>
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        <InputBaseContainer>
          <InputTextWithLabel
            name="name"
            error={errors.name}
            inputRef={register}
            label={labels.name}
            isRequired
          />
        </InputBaseContainer>

        <InputBaseContainer>
          <InputTextWithLabel
            name="permissionType"
            error={errors.permissionType}
            inputRef={register}
            label={labels.permissionType}
            isRequired
          />
        </InputBaseContainer>

        <InputBaseContainer>
          <TextAreaWithLabel
            name="description"
            error={errors.description}
            inputRef={register as any}
            label={labels.description}
            rowsMin={3}
          />
        </InputBaseContainer>

        <Field>
          <Label htmlFor="special-permission-user">
            {labels.assignedUser}
          </Label>
          <Controller
            name="assignedUser"
            control={control}
            defaultValue={initialValues?.assignedUser ?? ''}
            render={({ onChange, onBlur, value }) => (
              <Select
                id="special-permission-user"
                value={value === null || value === undefined ? '' : String(value)}
                onBlur={onBlur}
                onChange={e =>
                  onChange(e.target.value === '' ? null : e.target.value)}
              >
                <option value="">
                  Nobody - automation is paused
                </option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {displayName(user)}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.assignedUser && (
            <FieldError>
              {(errors.assignedUser as any)?.message ||
                'This user cannot hold a special permission.'}
            </FieldError>
          )}
        </Field>

        <InputBaseContainer>
          <Controller
            name="isActive"
            control={control}
            valueName="checked"
            defaultValue={initialValues?.isActive ?? true}
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
                error={errors.isActive}
                label={labels.isActive}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
              />
            )}
          />
        </InputBaseContainer>

        <ButtonWithLoader
          disabled={isSubmitting && !modalError}
          loading={isSubmitting && !modalError}
          type="submit"
        >
          {isNew ? addButtonText : updateButtonText}
        </ButtonWithLoader>
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;
  text-align: left;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const Field = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const FieldError = styled.div`
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--danger-500);
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: SpecialPermissionType;
  onSubmit: (data: SpecialPermissionType) => void;
  initialErrors?: FormServerError<SpecialPermissionType>;
  modalError: boolean;
}

export default SpecialPermissionForm;
